<template>
  <div v-if="visible && note" class="modal-overlay">
    <div class="modal-content">
      <button class="close-button" @click="closeModal" :disabled="isUpdating">×</button>

      <h2 class="modal-title">Rename Note</h2>

      <!-- 笔记标题 -->
      <div class="input-group">
        <label class="input-label">Note Title</label>
        <input type="text" class="note-input" v-model="noteTitle" @input="handleTitleChange" :disabled="isUpdating">
        <div v-if="titleError" class="error-message">{{ titleError }}</div>
      </div>

      <!-- 保存按钮 -->
      <button class="save-button" @click="updateNoteTitle" :disabled="!isValid || isUpdating">
        <i class="el-icon-check"></i>
        <span v-if="!isUpdating">Save</span>
        <span v-else class="loading-spinner"></span>
      </button>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'PopupNoteRename',
  props: {
    note: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      visible: false,
      noteTitle: '',
      isUpdating: false,
      titleError: '',
      debouncedValidate: null
    }
  },
  created() {
    this.debouncedValidate = debounce(this.validateTitle, 300)
  },
  computed: {
    isValid() {
      return this.noteTitle.trim() !== '' && !this.titleError
    }
  },
  methods: {
    openModal() {
      if (!this.note) return
      this.visible = true
      this.noteTitle = this.note.notename
      this.titleError = ''

      this.$nextTick(() => {
        const inputEl = this.$el.querySelector('.note-input')
        if (inputEl) {
          inputEl.focus()
          // 选中所有文本
          inputEl.select()
        }
      })
    },
    closeModal() {
      this.visible = false
      this.titleError = ''
    },
    handleTitleChange() {
      this.debouncedValidate()
    },
    validateTitle() {
      if (!this.noteTitle.trim()) {
        this.titleError = 'Note title cannot be empty'
        return false
      }
      this.titleError = ''
      return true
    },
    async updateNoteTitle() {
      if (!this.validateTitle() || !this.isValid) return
      
      try {
        this.isUpdating = true
        
        this.$emit('noteRenamed', {
          note: {
            ...this.note,
            notename: this.noteTitle.trim()
          }
        })

        // 调用API更新笔记标题
        await this.$axios.post('/api/updatenotename/', {
          noteid: this.note.noteid,
          notename: this.noteTitle.trim(),
        })
        
        this.$message.success('Note renamed successfully')
        this.closeModal()
      } catch (error) {
        console.error('Error updating note title:', error)
        this.$message.error(error.response?.data?.messages || 'Failed to rename note')
      } finally {
        this.isUpdating = false
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 450px;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  animation: fadeIn 0.3s ease, slideIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(20px);
  }
  to {
    transform: translateY(0);
  }
}

.close-button {
  position: absolute;
  right: 20px;
  top: 20px;
  background: none;
  border: none;
  color: #666666;
  font-size: 24px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s ease;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-button:hover {
  opacity: 1;
  background: #f5f5f5;
  color: #333333;
}

.close-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-title {
  color: #1a1a1a;
  font-size: 24px;
  margin: 0 0 28px 0;
  font-weight: 600;
}

.input-group {
  margin-bottom: 24px;
}

.input-label {
  display: block;
  color: #2c3e50;
  font-size: 15px;
  margin-bottom: 12px;
  font-weight: 500;
}

.note-input {
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  color: #333333;
  font-size: 15px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.note-input:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
  background-color: #ffffff;
}

.error-message {
  color: #ff4d4f;
  font-size: 14px;
  margin-top: 8px;
}

.save-button {
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  background-color: #4a90e2;
  color: white;
  font-weight: 600;
  cursor: pointer;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.2);
  margin-top: 8px;
}

.save-button:hover:not(:disabled) {
  background-color: #5a9ee2;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
}

.save-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style> 